import { useEffect, useRef, useState } from "react";

// Cap how often a growing markdown string is re-parsed. The drip can still
// advance every frame; consumers that feed react-markdown should throttle
// so we don't run remark/rehype at 60fps.
export function useThrottledValue(value, ms = 40, enabled = true) {
    const [throttled, setThrottled] = useState(value);
    const latestRef = useRef(value);
    latestRef.current = value;

    useEffect(() => {
        if (!enabled) {
            setThrottled(value);
            return undefined;
        }
        setThrottled(value);
        const id = setInterval(() => {
            setThrottled((prev) => (
                prev === latestRef.current ? prev : latestRef.current
            ));
        }, ms);
        return () => clearInterval(id);
    }, [enabled, ms]);

    useEffect(() => {
        if (!enabled) setThrottled(value);
    }, [enabled, value]);

    return enabled ? throttled : value;
}

// Reveal streamed text at a steady cadence so bursty SSE (or model) chunks
// still read like Open WebUI / Ollama — continuous typing instead of
// "dump a phrase, pause, dump a phrase". While `enabled`, we chase `target`
// with rAF; when disabled we snap to the full string.
export default function useSmoothReveal(target = "", enabled = false) {
    const [displayed, setDisplayed] = useState(target);
    const targetRef = useRef(target);
    const enabledRef = useRef(enabled);
    targetRef.current = target ?? "";
    enabledRef.current = enabled;

    useEffect(() => {
        if (!enabled) {
            setDisplayed(targetRef.current);
            return undefined;
        }

        let rafId = 0;
        const tick = () => {
            const want = targetRef.current;
            setDisplayed((cur) => {
                if (!want) return "";
                // Target rewound / replaced (new turn) — snap.
                if (cur && !want.startsWith(cur)) return want;
                if (cur.length >= want.length) return want;
                const lag = want.length - cur.length;
                // Catch up when far behind so long stalls don't trail forever;
                // drip 1–3 chars when close for a typewriter feel.
                const step = lag > 160 ? Math.ceil(lag / 3)
                    : lag > 60 ? 10
                        : lag > 20 ? 4
                            : lag > 6 ? 2
                                : 1;
                return want.slice(0, cur.length + step);
            });
            if (enabledRef.current) {
                rafId = requestAnimationFrame(tick);
            }
        };
        rafId = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafId);
    }, [enabled]);

    useEffect(() => {
        if (!enabled) setDisplayed(target ?? "");
    }, [enabled, target]);

    return enabled ? displayed : (target ?? "");
}
